/**
 * Alkahest TTS Provider
 * Extracted and adapted from Phantasy AudioService
 */

import { BaseTTSProvider } from './ITTSProvider';
import { createLogger } from '../../utils/logger';
import type { TTSProviderConfig } from '../../types/config';

export class AlkahestProvider extends BaseTTSProvider {
  private logger: ReturnType<typeof createLogger>;

  constructor(config: TTSProviderConfig) {
    super(config);
    this.logger = createLogger('AlkahestProvider');
  }

  getProviderName(): string {
    return 'alkahest';
  }

  async generateAudio(text: string): Promise<Blob> {
    try {
      this.logger.info('Generating audio with Alkahest for text:', text);

      const cleanedText = this.validateText(text);
      this.logger.debug('Cleaned text:', cleanedText);

      const url = `${this.config.apiUrl}/audio/speech`;
      const requestBody = {
        model: this.config.model || 'tts-1',
        input: cleanedText,
        voice: this.config.voice || 'rally',
        speed: this.config.speed || 1.0,
        response_format: this.config.format || 'wav',
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'audio/wav',
      };

      // Add API key if provided
      if (this.config.apiKey) {
        headers['Authorization'] = `Bearer ${this.config.apiKey}`;
      }

      this.logger.debug('Alkahest request:', { 
        url, 
        headers: { ...headers, Authorization: headers.Authorization ? '[REDACTED]' : undefined },
        body: requestBody,
        hasApiKey: !!this.config.apiKey,
      });

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`Alkahest API error (${response.status}): ${errorText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      this.logger.info('Alkahest audio generated successfully');
      return new Blob([arrayBuffer], { type: 'audio/wav' });

    } catch (error) {
      this.logger.error('Error in Alkahest generation:', error);
      throw error;
    }
  }
}